import React from 'react';
import styled from 'styled-components';
import { Vector3 } from 'three';

import { CompositeProgressController } from '@frontend/controllers';
import { MultiMap } from '@frontend/datatypes';

import ViewPort from './ViewPort';
import {
  COLORS,
  COLOR_CODES,
  ENTITY_TYPES,
  FORCE_VIEW_TYPES,
  OPACITY_MAP,
  STOCK_TYPES,
  PART_VIEWER_MODES,
} from './constants';
import { areFacesEqual } from './geom-utils';
import { constructEntityName } from './loaders/step/utils';
import { encodeObject, encodeViewerState } from './renderer/load-utils';
import RendererComponent, {
  VECTOR_EDITOR_MODES,
} from './renderer/renderer-component';
import { rendererDefaultProps } from './renderer/renderer-component';
import BarStockLegend from './ui/BarStockLegend';
import BoxSelect from './ui/BoxSelect';
import InteractionPopover from './ui/InteractionPopover';
import LoadingIndicator from './ui/LoadingIndicator';
import VectorizationPopover from './ui/VectorizationPopover';
import { ViewerAlerts } from './ui/ViewerAlerts';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;

  ${(props) => (props.viewNone ? `display: none;` : '')};
`;

const HOVER_CONFLICT_TYPES = {
  SELECTION: 'SELECTION',
  CALIPER: 'CALIPER',
};

const getInteractedObjectKey = ({ type, obj, face }) =>
  type === ENTITY_TYPES.STL
    ? constructEntityName(obj.name, (face || {}).a)
    : obj.name;

//For STL, by convention faces are looked up by their vertex a's index in BufferGeometry positions
const getGeomIndex = (type, face, obj) =>
  type === ENTITY_TYPES.STL ? face.a : obj.index;

const getHighlightForSelection = (
  objects,
  stlHex,
  faceHex,
  edgeHex,
  vertexHex,
  opacity,
) =>
  objects.map(({ type, face, obj, body }) => ({
    body,
    type,
    color:
      type === ENTITY_TYPES.STL
        ? stlHex
        : type === ENTITY_TYPES.EDGE
        ? edgeHex
        : type === ENTITY_TYPES.VERTEX
        ? vertexHex
        : faceHex,
    opacity,
    visibility: true,
    indices: [getGeomIndex(type, face, obj)],
  }));

// TODO: clean this up with color codes
const getHoverUnconflictedHighlightColor = (hoverObjectType, viewerMode) => {
  switch (hoverObjectType) {
    case ENTITY_TYPES.FACE:
      return COLOR_CODES.highlight_color;
    case ENTITY_TYPES.EDGE:
      return viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
        ? 0xd408d2
        : COLOR_CODES.edge_highlight_color;
    case ENTITY_TYPES.VERTEX:
      return COLOR_CODES.edge_highlight_color;
    case ENTITY_TYPES.STL:
      return COLOR_CODES.stl_highlight_color;
    default:
      return COLOR_CODES.highlight_color;
  }
};

// TODO: clean this up with color codes
const getHoverConflictedHighlightColor = (
  hoverObjectType,
  viewerMode,
  conflictType,
) => {
  switch (hoverObjectType) {
    case ENTITY_TYPES.FACE:
      switch (conflictType) {
        case HOVER_CONFLICT_TYPES.SELECTION:
          return 0xadff73; //green
        case HOVER_CONFLICT_TYPES.CALIPER:
          return 0x8ba2f7; //bluish
      }
      break;
    case ENTITY_TYPES.EDGE:
      switch (conflictType) {
        case HOVER_CONFLICT_TYPES.SELECTION:
          return viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
            ? 0x33a100 //dark green
            : 0x24ad29; //another dark green
        case HOVER_CONFLICT_TYPES.CALIPER:
          return viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
            ? COLORS.blue
            : 0x4592ff; //bluish
      }
      break;
    case ENTITY_TYPES.VERTEX:
      return 0xadff73; //bright greenish
    case ENTITY_TYPES.STL:
      switch (conflictType) {
        case HOVER_CONFLICT_TYPES.SELECTION:
          return 0xadff73; //bright greenish
        case HOVER_CONFLICT_TYPES.CALIPER:
          return 0x6e8cff; //bluish
      }
      break;
    default:
      return COLOR_CODES.highlight_color;
  }
};

const getHoverHighlightColor = (
  { type, obj, face }, //hoverObject
  viewerMode,
  selectedObjKeys,
  caliperObjKeys,
) => {
  const key = getInteractedObjectKey({ type, obj, face });
  if (selectedObjKeys.has(key)) {
    return getHoverConflictedHighlightColor(
      type,
      viewerMode,
      HOVER_CONFLICT_TYPES.SELECTION,
    );
  } else if (caliperObjKeys.has(key)) {
    return getHoverConflictedHighlightColor(
      type,
      viewerMode,
      HOVER_CONFLICT_TYPES.CALIPER,
    );
  } else {
    return getHoverUnconflictedHighlightColor(type, viewerMode);
  }
};

const getHighlightForHover = (
  hoverObjects,
  viewerMode,
  selectedObjKeys,
  caliperObjKeys,
) =>
  (hoverObjects || []).reduce((acc, { type, face, obj, body }) => {
    //SimpReps are not highlightable
    if (type !== ENTITY_TYPES.SimpRep) {
      acc.push({
        body,
        type,
        color: getHoverHighlightColor(
          { type, obj, face },
          viewerMode,
          selectedObjKeys,
          caliperObjKeys,
        ),
        opacity:
          viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
            ? 1.0
            : OPACITY_MAP.HOVER_OBJECT,
        visibility: true,
        indices: [getGeomIndex(type, face, obj)],
      });
    }
    return acc;
  }, []);

// TODO: convert this to a useEffect watching for selectedObjects, historicalSelectedObjects,
// caliperObjects, historicalCaliperObjects, and hoverObjects to update the highlight state
const getInternalHighlight = (
  //From viewer component's props
  {
    showHoverObject,
    showSelectedObjects,
    activeSelectorTool,
    activeCaliperTool,
    viewerMode,
    editorMode,
  },
  //The following are from viewer component's current state and next entities or hover state
  {
    hoverObjects = [],
    historicalSelectedObjects = [],
    selectedObjects = [],
    historicalCaliperObjects = [],
    caliperObjects = [],
  },
) => [
  // Selected Highlights:
  // * handle historical selections then active selections
  ...(showSelectedObjects
    ? getHighlightForSelection(
        historicalSelectedObjects,
        COLOR_CODES.historical_selected_color,
        COLOR_CODES.historical_selected_color,
        COLOR_CODES.historical_selected_color,
        COLOR_CODES.historical_selected_color,
        viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
          ? 1.0
          : OPACITY_MAP.SELECTED_OBJECT,
      )
    : []),

  ...(showSelectedObjects &&
  (activeSelectorTool || editorMode === VECTOR_EDITOR_MODES.SCALE)
    ? getHighlightForSelection(
        selectedObjects,
        COLOR_CODES.stl_selected_color,
        COLOR_CODES.selected_color,
        COLOR_CODES.selected_color,
        COLOR_CODES.selected_color,
        viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
          ? 1.0
          : OPACITY_MAP.SELECTED_OBJECT,
      )
    : []),

  ...(showSelectedObjects
    ? getHighlightForSelection(
        historicalCaliperObjects,
        COLOR_CODES.caliper_faces, //stl
        COLOR_CODES.caliper_faces, //faces
        COLOR_CODES.caliper_edges, //edges
        COLOR_CODES.caliper_vertices, //vertices
        // always show selection highlights in vector editor (allows highlights to persist during "shift-click" measurement select hover)
        // handle historical caliper objects then active caliper selections
        viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
          ? 1.0
          : OPACITY_MAP.SELECTED_OBJECT,
      )
    : []),

  ...(showSelectedObjects && activeCaliperTool
    ? getHighlightForSelection(
        caliperObjects,
        COLOR_CODES.caliper_faces, //stl
        COLOR_CODES.caliper_faces, //faces
        COLOR_CODES.caliper_edges, //edges
        COLOR_CODES.selected_color,
        viewerMode === PART_VIEWER_MODES.VECTOR_EDIT
          ? 1.0
          : OPACITY_MAP.SELECTED_OBJECT,
      )
    : []),

  // apply highlight of hover after taking into account collisions
  ...(showHoverObject
    ? getHighlightForHover(
        hoverObjects,
        viewerMode,
        //selectedObjKeys:
        new Set(
          showSelectedObjects
            ? [
                ...historicalSelectedObjects,
                ...(activeSelectorTool ||
                editorMode === VECTOR_EDITOR_MODES.SCALE
                  ? selectedObjects
                  : []),
              ].map(getInteractedObjectKey)
            : [],
        ),

        //caliperObjKeys:
        new Set(
          showSelectedObjects
            ? [
                ...historicalCaliperObjects,
                ...(activeCaliperTool ? caliperObjects : []),
              ].map(getInteractedObjectKey)
            : [],
        ),
      )
    : []),
];

const areHoverObjectsEquivalent = (a, b) => {
  if (a.obj && b.obj) {
    if (a.obj.name === b.obj.name) {
      if (a.type === ENTITY_TYPES.STL) {
        return areFacesEqual(a.face, b.face);
      } else {
        return true;
      }
    } else {
      return false;
    }
  } else {
    return Boolean(a.obj) === Boolean(b.obj);
  }
};

const areHoverObjectArraysEquivalent = (a, b) => {
  if (a.length === b.length) {
    for (let i = 0; i < a.length; i++) {
      if (!areHoverObjectsEquivalent(a[i], b[i])) {
        return false;
      }
    }
    return true;
  } else {
    return false;
  }
};

class ViewerComponent extends React.Component {
  static defaultProps = {
    activeCaliperTool: false,
    activeSelectorTool: false,
    isPaused: false,
    isPMIViewActive: false,
    onlyShowHighlighted: false,
    showDimensions: false,
    showHoverObject: false,
    showSelectedObjects: true,
    ...rendererDefaultProps,
  };

  //Why deferredSetState() ?
  //* See PP-18408 and  PP-18409
  //* The viewer's handling of observations of prop changes expects
  //  some changes to happen in separate updates.
  //  ie: Separate calls of `renderer:updateState()` (renderer.ts)
  //* Prior to React 18 this was not a problem
  //* React 18 groups multiple state changes into a single re-render
  //  which breaks `renderer:updateState()` expectation that internalHighlight
  //  happen in a future call.
  //* A quick solution is to wrap the update into a setTimeout(), which
  //  forces a 2nd render as it did prior to React 18
  //* In the future, `renderer:updateState()` needs re-designing. And we need
  //  to re-think how we observe changes from react
  deferredSetState = (stateOfSetStateFn) =>
    new Promise((resolve) => {
      setTimeout(() => {
        this.setState(stateOfSetStateFn);
        resolve();
      });
    });

  state = {
    loadingProgress: new CompositeProgressController({
      //* This is fallback description that displays if the loadingProgress is visible
      //  but active child progresses (if any) don't have a description
      //* For a short moment the constiuent progress controllers are unknown until the first part data arrives
      //  Once the first data about the part arrives, the next loading steps begin and their description is used
      id: 'loadingProgressRoot',
      description: 'Loading part data...',
    }),
    bodies: [],
    assemblyInstances: [],
    partOrAssemblyById: new Map(),
    bodyOrAssemblyInstanceById: new Map(),
    bodyIdsByPartFileUuid: new MultiMap(),
    // mouse hover interaction information
    hoverObjects: [], // three.js+ entity for the hover object

    // scene management for tracking current view state
    cameraPosition: new Vector3(),
    cameraUp: new Vector3(),
    cameraZoom: 1,
    controlTarget: new Vector3(),
    orthoView: new ViewPort(),
    bodyPoseInformation: {},
    maxBoundingBoxDiagonal: 0, // used for resolution of camera zoom as more bodies load in

    // selected object entities
    // NOTE: we do not update the following 4 inline with setState actions because they are managed through
    // simulated useEffect interactions in our renderer.updateState method. We need these here to differentiate
    // from the props that drive these because we do not want to store three.js entities outside of this state
    // The objects that come in as props described in CADViewer/data/reducer are encoded versions of these
    // objects as they exist in three.js memory
    caliperObjects: [], // these are the actual three.js objects of caliper selections
    historicalCaliperObjects: [], // these are the actual three.js objects of historical caliper selections
    selectedObjects: [], // these are the actual three.js objects of selector tool selections
    historicalSelectedObjects: [], // these are the actual three.js objects of historical selector tool selections

    internalHighlight: [], // highlight that has to do with showing colors for hover, selected, and caliper objects

    normals: [],
    measurement: null,

    // loading state variables
    hasLoaded: false,

    // details on interactability permissions and adjustments
    tweeningDetails: {
      position: false,
      target: false,
      zoom: false,
      viewport: false,
      up: false,
      emissivity: false,
      opacity: false,
    },
    isTweening: false,
    isFocused: true,
    nodeWidth: null,
    nodeHeight: null,

    // popover information
    isPopoverOpen: false,
    popoverIntersection: null,
    popoverPosition: {
      top: null,
      left: null,
    },

    temporarilySimplified: new Set(), //bodyIds
    tooComplex: new Set(), //part ids
    failed: new Set(), //filenames

    // information for drawing boxes on top level canvas for box select
    boxSelectStartPoint: { x: null, y: null },
    boxSelectCurrentPoint: { x: null, y: null },
  };

  //Note handleObjectHover is never called. (exception vector editor)
  //* It is bound to `onObjectHover`, which can be traced to renderer.js.  See notes there that
  //  explain why it is never called.
  //* There is code here that inspects `showHoverObject`, but renderer.js does not because showHoverObject
  //  does not propogate to renderer.js when it changes.
  //* If we want to call this, see notes in renderer-component and renderer.js
  //Although there is an exception for vector editor, the vector editor doesn't provide the partviewer onNewHoverPoint property
  //so again it has no effect...
  handleObjectHover = (nextHoverObjects) => {
    if (this.props.onNewHoverPoint) {
      const firstObject = nextHoverObjects[0];
      const nextPoint = firstObject ? firstObject.point : null;
      const nextNormal =
        firstObject && firstObject.face && firstObject.face.normal;
      this.props.onNewHoverPoint({
        hoverPoint: { point: nextPoint, normal: nextNormal },
      });
    }

    if (
      !areHoverObjectArraysEquivalent(nextHoverObjects, this.state.hoverObjects)
    ) {
      const props = this.props;
      this.setState((state) => ({
        hoverObjects: nextHoverObjects,
        internalHighlight: getInternalHighlight(props, {
          ...state,
          hoverObjects: nextHoverObjects,
        }),
      }));
    }
  };

  handleObjectClickForCaliperTool = ({
    body,
    face,
    obj,
    normal,
    point,
    type,
  }) => {
    const newSelections = [];
    if (obj) {
      if (this.state.caliperObjects.length === 0) {
        newSelections.push({ body, face, obj, normal, point, type });
      } else if (this.state.caliperObjects.length === 1) {
        const firstCaliperObject = this.state.caliperObjects[0];
        if (firstCaliperObject.obj !== obj) {
          // now need to queue up both making sure the original is first
          newSelections.push(firstCaliperObject);
          newSelections.push({ body, face, obj, normal, point, type });
        } else if (
          firstCaliperObject.obj === obj &&
          firstCaliperObject.type === ENTITY_TYPES.STL &&
          type === ENTITY_TYPES.STL &&
          !areFacesEqual(face, firstCaliperObject.face)
        ) {
          // stl selecting faces on same object
          newSelections.push(firstCaliperObject);
          newSelections.push({ body, face, obj, normal, point, type });
        }
      } else if (this.state.caliperObjects.length === 2) {
        const [firstCaliperObject, secondCaliperObject] =
          this.state.caliperObjects;
        // if the second entry is equal to the new object, dont unselect the second but leave the first
        // if this new entry is equal to the first object, unselect the first and make the second one first in the queue
        // if the new entry is different from both, make that the only selected object
        // other wise, it will clear itself
        const firstEqual =
          firstCaliperObject.type === ENTITY_TYPES.STL
            ? face && areFacesEqual(face, firstCaliperObject.face)
            : firstCaliperObject.obj === obj;
        const secondEqual =
          secondCaliperObject.type === ENTITY_TYPES.STL
            ? face && areFacesEqual(face, secondCaliperObject.face)
            : secondCaliperObject.obj === obj;
        if (firstEqual) {
          newSelections.push(secondCaliperObject);
        } else if (secondEqual) {
          newSelections.push(firstCaliperObject);
        } else {
          newSelections.push({ body, face, obj, normal, point, type });
        }
      }
    }
    if (this.props.onCaliperObjectSelect) {
      this.props.onCaliperObjectSelect({
        selections: newSelections.map((sel) => encodeObject(sel)),
        viewerState: encodeViewerState(this.state),
      });
    }
    if (this.props.onNewMetaData) {
      const metaData = newSelections.length
        ? newSelections[newSelections.length - 1].obj.metaData
        : {};
      this.props.onNewMetaData({ metaData });
    }

    // update normals state inline, but do not update caliperObjects state inline, as that is handled
    // by prop updating from parent component
    this.setState({
      normals:
        obj && (type === ENTITY_TYPES.FACE || type === ENTITY_TYPES.STL)
          ? [{ point, normal }]
          : [],
    });
  };

  handlePseudoObjectClickForCaliperTool = ({
    body,
    face,
    obj,
    normal,
    point,
    type,
  }) => {
    const newSelections = [];
    if (obj && this.state.selectedObjects.length === 1) {
      const firstCaliperObject = this.state.selectedObjects[0];
      if (firstCaliperObject.obj.name !== obj.name) {
        // now need to queue up both making sure the original is first
        newSelections.push(firstCaliperObject);
        newSelections.push({ body, face, obj, normal, point, type });
      }
      // clear selector object
      this.props.onSelectorObjectDeselect(
        this.props.selectorToolSelections[0].selectedObject,
      );
      this.props.onCaliperObjectSelect({
        selections: newSelections.map((sel) => encodeObject(sel)),
        viewerState: encodeViewerState(this.state),
      });
    } else if (this.state.caliperObjects.length === 2) {
      // clear caliper objects
      this.props.onCaliperObjectSelect({
        selections: [],
        viewerState: encodeViewerState(this.state),
      });
    }
    const metaData = newSelections.length
      ? newSelections[newSelections.length - 1].obj.metaData
      : {};
    this.props.onNewMetaData({ metaData });
    // skip setting normals (never of type ENTITY_TYPES.FACE or ENTITY_TYPES.STL)
  };

  handleObjectClickForSelectorTool = ({ body, face, obj, point, type }) => {
    if (!obj) {
      return;
    }
    let alreadySelectedObj = null;
    const encodedObject = encodeObject({ body, face, obj, point, type });
    if (type === ENTITY_TYPES.STL) {
      alreadySelectedObj = this.state.selectedObjects.find((selectedObject) =>
        areFacesEqual(selectedObject.face, face),
      );
    } else {
      alreadySelectedObj = this.state.selectedObjects.find(
        (selectedObject) => selectedObject.obj.name === encodedObject.obj,
      );
    }
    // update surround props, but do not update selectedObjects state inline, as that is handled
    // by prop updating from parent component
    const isAlreadySelected = Boolean(alreadySelectedObj);
    if (isAlreadySelected) {
      if (this.props.onSelectorObjectDeselect) {
        this.props.onSelectorObjectDeselect({
          obj: encodedObject.obj,
          f: encodedObject.f,
        });
      }
      // clear meta data on removal
      if (this.props.onNewMetaData) {
        this.props.onNewMetaData({ metaData: {} });
      }
    } else {
      // remove all selected objects from viewerState, we are only interested in 'snap to' functionality
      if (this.props.onSelectorObjectSelect) {
        const selection = {
          selectedObject: encodedObject,
          viewerState: encodeViewerState(this.state),
        };
        this.props.onSelectorObjectSelect({ selection });
      }
      // set the meta data for new selection
      if (this.props.onNewMetaData) {
        this.props.onNewMetaData({ metaData: obj.metaData });
      }
    }
  };

  handleRightClickForPopover = ({ intersectionTest, pageX, pageY }) => {
    this.setState({
      isPopoverOpen: true,
      popoverPosition: {
        left: pageX,
        top: pageY,
      },
      popoverIntersection: intersectionTest,
    });
  };

  onSimplified = async ({
    temporarilySimplified,
    temporarilySimplifiedParts,
    tooComplex,
  }) => {
    if (temporarilySimplified !== this.state.temporarilySimplified) {
      this.setState({ temporarilySimplified });
      if (this.props.receiveTemporarilySimplified) {
        this.props.receiveTemporarilySimplified({
          temporarilySimplified,
          temporarilySimplifiedParts,
        });
      }
    }
    if (tooComplex !== this.state.tooComplex) {
      this.setState({ tooComplex });
      if (this.props.receiveTooComplexForPartViewer) {
        this.props.receiveTooComplexForPartViewer({ tooComplex });
      }
    }
  };

  closePopover = () => {
    if (this.state.isPopoverOpen) {
      this.setState({
        isPopoverOpen: false,
      });
    }
  };

  handleBoxSelectStart = ({ pageX, pageY }) => {
    this.setState({
      boxSelectStartPoint: { x: pageX, y: pageY },
    });
  };

  handleBoxSelectMove = ({ pageX, pageY }) => {
    this.setState({
      boxSelectCurrentPoint: { x: pageX, y: pageY },
    });
  };

  handleBoxSelectClear = () => {
    this.setState({
      boxSelectStartPoint: { x: null, y: null },
      boxSelectCurrentPoint: { x: null, y: null },
    });
  };

  handleBoxSelect = ({ selections }) => {
    if (selections.length > 0 && this.props.onNewMetaData) {
      this.props.onNewMetaData({ metaData: selections[0].obj.metaData });
    }

    const viewerState = encodeViewerState(this.state);

    if (this.props.onSelectorObjectBoxSelect) {
      this.props.onSelectorObjectBoxSelect({
        selections: selections.map(({ body, face, obj, point, type }) => {
          const encodedObject = encodeObject({ body, face, obj, point, type });
          return {
            selectedObject: encodedObject,
            viewerState,
          };
        }),
      });
    }
  };

  handleBoxDeselect = ({ selections }) => {
    if (selections.length > 0 && this.props.onNewMetaData) {
      this.props.onNewMetaData({ metaData: selections[0].obj.metaData });
    }

    if (this.props.onSelectorObjectsDeselect) {
      this.props.onSelectorObjectsDeselect({
        objs: selections.map(({ body, face, obj, point, type }) => {
          const encodedObject = encodeObject({ body, face, obj, point, type });
          return encodedObject.obj;
        }),
      });
    }
  };

  handleSetMeasurement = (measurementItem) => {
    if (measurementItem) {
      if (this.props.onNewMeasurement) {
        this.props.onNewMeasurement({ distance: measurementItem.rawLength });
      }
      if (this.props.onNewMeasurementComparison) {
        this.props.onNewMeasurementComparison({
          comparison: measurementItem.comparison || null,
        });
      }
    }
    // Allows setting measurement to null (no active measurement State)
    this.setState({
      measurement: measurementItem,
    });
  };

  handleCameraPositionChange = (newPosition) => {
    this.setState({
      cameraPosition: newPosition,
    });
  };

  handleCameraUpChange = (newUp) => {
    this.setState({
      cameraUp: newUp,
    });
  };

  handleCameraZoomChange = (newZoom) =>
    this.deferredSetState({
      cameraZoom: newZoom,
    });

  handleTargetChange = (newTarget) => {
    this.setState({
      controlTarget: newTarget,
    });
  };

  handleOrthoViewChange = (view) => {
    this.setState({
      orthoView: view,
    });
  };

  handleBodyPoseChange = (newBodyPose) =>
    this.deferredSetState({
      bodyPoseInformation: newBodyPose,
    });

  handleMaxBoundingBoxDiagonalChange = (newDiagonal) => {
    this.setState({
      maxBoundingBoxDiagonal: newDiagonal,
    });
  };

  handleUpdateSelectionEntities = (nextEntities) => {
    const props = this.props;
    this.setState((state) => ({
      ...nextEntities,
      internalHighlight: getInternalHighlight(props, {
        ...state,
        ...nextEntities,
      }),
    }));
  };

  onHasLoaded = ({ hasLoaded }) => {
    if (this.state.hasLoaded !== !!hasLoaded) {
      this.setState({
        hasLoaded,
      });
    }
  };

  acknowledgeSnapTo = (acknowledgedPose) => {
    if (this.props.setAcknowledgedForcedViewerPose) {
      this.props.setAcknowledgedForcedViewerPose(acknowledgedPose);
    }
  };

  handleFocusChange = (value) => {
    this.setState({
      isFocused: value,
    });
  };

  handleNodeChange = (newWidth, newHeight) => {
    this.setState({
      nodeWidth: newWidth,
      nodeHeight: newHeight,
    });
  };

  constructor(props) {
    super(props);
  }

  componentWillUnmount() {
    this.state.loadingProgress.clear();
    // delete this.state.loadingProgress;
    for (const key of Object.keys(this.state)) {
      delete this.state[key];
    }
  }

  render() {
    const {
      activeBodies,
      pmiUrlByPartFileUuid,
      activeCaliperTool,
      activeSelectorTool,
      showAxisAlignedBoundingBox, //Boolean: isDimensionToolActive (the axis aligned dimensions button is enabled or not) (same as showDimensions)
      caliperObjectsKeys,
      caliperToolSelections,
      clearAllSelections,
      fetchViewerData,
      onLoadPartFile,
      pendingPartFileUuids,
      geometricAnalysisData, //TODO: Don't need geometricAnalysisData.interrogationFiles or geometricAnalysisData.setups
      displayScale,
      displayUnits,
      dimensionalPrecision,
      enableSelectorTool, // additionally used to set selector tool to active for new vector editor
      env,
      explodedState,
      failedParts,
      featureSnapTo,
      focusedBodies,
      focusOnBodies,
      forcedViewerPose,
      forceFocus,
      forceView,
      hideViewerBody,
      highlight,
      isFocusedViewActive,
      leftOffset,
      showOptimalBoundingBox,
      isPaused,
      isPMIViewActive,
      isSelectorToolActive,
      materialStockResults,
      nativeUnits,
      onlyShowHighlighted,
      onNestingCreationFinish,
      onNewMetaData,
      onNewSelectorCumulativeInfo,
      onSelectorObjectSelect,
      outerReceiveFocusedUuid,
      receiveHasPMIAnnotations,
      receiveForceView,
      receiveViewerState,
      receiveVisibleBodies,
      renderNativeColors,
      selectedBodies,
      updatedVectors,
      updatedVectorsCounter,
      initializeVectorStyleByNameMap,
      getAssignToLayerOptions,
      selectionsCount,
      handleMoveTo,
      handleOnIsolate,
      handleOnInvert,
      handleOnDelete,
      isDxfFinalizing,
      receiveSerializedDXF,
      selectedObjectsKeys,
      selectorToolSelections,
      setReadyForInteraction,
      showAdditionalKeys,
      showAdditionalKeysExplicit,
      showSelectedObjects,
      showHoverObject,
      shouldCaptureViewerState,
      stockCreationFailed,
      stockCreationSuccess,
      useCache,
      viewerMode,
      editorMode,
      visibleBodies,
      visibleStockResultKeys,
      wireframe,
    } = this.props;

    const {
      boxSelectCurrentPoint,
      boxSelectStartPoint,
      hasLoaded,
      isPopoverOpen,
      popoverIntersection,
      popoverPosition,
    } = this.state;

    if (shouldCaptureViewerState) {
      if (receiveViewerState) {
        receiveViewerState({ viewerState: encodeViewerState(this.state) });
      }
    }

    let shouldShowLegend = false;
    for (const result of materialStockResults) {
      if (
        result.stockType === STOCK_TYPES.RECTANGULAR ||
        result.stockType === STOCK_TYPES.CYLINDRICAL
      ) {
        if (visibleStockResultKeys.includes(result.key)) {
          shouldShowLegend = true;
          break;
        }
      }
    }

    const handleZoomToFit = (viewerId) => {
      if (!visibleBodies.includes(viewerId)) {
        receiveVisibleBodies({ visibleBodies: visibleBodies.concat(viewerId) });
      }
      receiveForceView({
        forceView: {
          type: FORCE_VIEW_TYPES.ZOOM_TO_FIT,
          payload: { viewerId },
        },
      });
      this.closePopover();
    };

    const {
      caliperObjects,
      historicalCaliperObjects,
      historicalSelectedObjects,
      selectedObjects,
    } = this.state;
    const allSelectedEntities = [
      ...caliperObjects,
      ...historicalCaliperObjects,
      ...historicalSelectedObjects,
      ...selectedObjects,
    ];
    const canZoomToSelection = allSelectedEntities.length > 0;
    const handleZoomToSelection = () => {
      const relatedViewerIds = allSelectedEntities.map((entity) => entity.body);
      const nextVisibleBodies = [...visibleBodies];
      let areAnyNotVisible = false;
      for (const viewerId of relatedViewerIds) {
        if (!visibleBodies.includes(viewerId)) {
          nextVisibleBodies.push(viewerId);
          areAnyNotVisible = true;
        }
      }
      if (areAnyNotVisible) {
        receiveVisibleBodies({ visibleBodies: nextVisibleBodies });
      }
      receiveForceView({
        forceView: { type: FORCE_VIEW_TYPES.ZOOM_TO_SELECTION },
      });
      this.closePopover();
    };

    const handleViewNormalTo = (intersection) => {
      receiveForceView({
        forceView: {
          type: FORCE_VIEW_TYPES.NORMAL_TO,
          payload: { intersection },
        },
      });
      this.closePopover();
    };

    const canClearSelection =
      selectedObjects.length > 0 && Boolean(clearAllSelections);
    const handleClearSelection = () => {
      clearAllSelections();
      this.closePopover();
    };

    const canCreateSelection =
      selectedObjects.length === 0 && Boolean(onSelectorObjectSelect);
    const handleCreateSelection = (popoverIntersection) => {
      if (!isSelectorToolActive) {
        enableSelectorTool();
      }
      this.handleObjectClickForSelectorTool({
        ...popoverIntersection,
        obj: popoverIntersection.object,
      });
      this.closePopover();
    };

    const handleFocus = (viewerBodyId) => {
      const body = this.state.bodyOrAssemblyInstanceById.get(viewerBodyId);
      focusOnBodies({ viewerBodyIds: [viewerBodyId] });
      if (outerReceiveFocusedUuid) {
        outerReceiveFocusedUuid({ uuid: body.part.id });
      }
      this.closePopover();
    };

    const handleFocusAllInstances = (viewerBodyId) => {
      const selectedBody =
        this.state.bodyOrAssemblyInstanceById.get(viewerBodyId);
      const { id } = selectedBody.part;
      const viewerBodyIds = this.state.bodyIdsByPartFileUuid.get(id);
      focusOnBodies({ viewerBodyIds });
      if (outerReceiveFocusedUuid) {
        outerReceiveFocusedUuid({ uuid: id });
      }
      this.closePopover();
    };

    const handleIsolate = (viewerId) => {
      receiveVisibleBodies({ visibleBodies: [viewerId] });
      this.closePopover();
    };

    const handleHide = (viewerBodyId) => {
      hideViewerBody({ viewerBodyId });
      this.closePopover();
    };

    // Show all bodies shows all bodies under the root assemblyInstance or Body of the intersected body
    const handleShowAllBodies = (viewerBodyId) => {
      focusOnBodies({ viewerBodyIds: [viewerBodyId] });
      this.closePopover();
    };

    return (
      <Wrapper ref={(node) => (this.wrapperRef = node)} viewNone={isPaused}>
        {shouldShowLegend && <BarStockLegend />}
        <LoadingIndicator
          leftOffset={leftOffset}
          isInitial={!hasLoaded}
          loadingProgress={this.state.loadingProgress.signal}
        />
        {viewerMode === PART_VIEWER_MODES.FULL && (
          <InteractionPopover
            bodyIdsByPartFileUuid={this.state.bodyIdsByPartFileUuid}
            canClearSelection={canClearSelection}
            canCreateSelection={canCreateSelection}
            canZoomToSelection={canZoomToSelection}
            closePopover={this.closePopover}
            handleClearSelection={handleClearSelection}
            handleCreateSelection={handleCreateSelection}
            handleFocus={handleFocus}
            handleFocusAllInstances={handleFocusAllInstances}
            handleHide={handleHide}
            handleIsolate={handleIsolate}
            handleShowAllBodies={handleShowAllBodies}
            handleViewNormalTo={handleViewNormalTo}
            handleZoomToFit={handleZoomToFit}
            handleZoomToSelection={handleZoomToSelection}
            isPopoverOpen={isPopoverOpen}
            popoverIntersection={popoverIntersection}
            popoverPosition={popoverPosition}
            isAssemblyOrSubAssembly={this.state.bodyIdsByPartFileUuid.size > 1}
            bodyOrAssemblyInstanceById={this.state.bodyOrAssemblyInstanceById}
            env={env}
          />
        )}
        {/* Check if handleMoveTo is defined to determine if this is the new vectorEditor -- don't display popover on old vectorFileEditor */}
        {viewerMode === PART_VIEWER_MODES.VECTOR_EDIT && handleMoveTo && (
          <VectorizationPopover
            getAssignToLayerOptions={getAssignToLayerOptions}
            selectionsCount={selectionsCount}
            editorMode={editorMode}
            handleMoveTo={handleMoveTo}
            handleOnIsolate={handleOnIsolate}
            handleOnInvert={handleOnInvert}
            handleOnDelete={handleOnDelete}
            closePopover={this.closePopover}
            isPopoverOpen={isPopoverOpen}
            popoverIntersection={popoverIntersection}
            popoverPosition={popoverPosition}
          />
        )}
        {activeSelectorTool && (
          <BoxSelect
            boxSelectCurrent={boxSelectCurrentPoint}
            boxSelectStart={boxSelectStartPoint}
          />
        )}
        <RendererComponent
          // ref props
          ref={(el) => (this.rendererComponent = el)}
          // direct props
          activeBodies={activeBodies}
          pmiUrlByPartFileUuid={pmiUrlByPartFileUuid}
          receiveHasPMIAnnotations={receiveHasPMIAnnotations}
          activeCaliperTool={activeCaliperTool}
          activeSelectorTool={activeSelectorTool}
          enableSelectorTool={enableSelectorTool}
          showAxisAlignedBoundingBox={showAxisAlignedBoundingBox}
          caliperObjectsKeys={caliperObjectsKeys}
          caliperToolSelections={caliperToolSelections}
          loadingProgress={this.state.loadingProgress}
          pendingPartFileUuids={pendingPartFileUuids}
          fetchViewerData={fetchViewerData}
          useCache={useCache}
          onLoadPartFile={onLoadPartFile}
          geometricAnalysisData={geometricAnalysisData}
          bodies={this.state.bodies}
          assemblyInstances={this.state.assemblyInstances}
          partOrAssemblyById={this.state.partOrAssemblyById}
          bodyOrAssemblyInstanceById={this.state.bodyOrAssemblyInstanceById}
          bodyIdsByPartFileUuid={this.state.bodyIdsByPartFileUuid}
          setReadyForInteraction={setReadyForInteraction}
          displayScale={displayScale}
          displayUnits={displayUnits}
          dimensionalPrecision={dimensionalPrecision}
          env={env}
          explodedState={explodedState}
          featureSnapTo={featureSnapTo}
          focusedBodies={focusedBodies}
          forceFocus={forceFocus}
          forceView={forceView}
          forcedViewerPose={forcedViewerPose}
          highlight={highlight}
          isFocusedViewActive={isFocusedViewActive}
          showOptimalBoundingBox={showOptimalBoundingBox}
          isPaused={isPaused}
          isPMIViewActive={isPMIViewActive}
          materialStockResults={materialStockResults}
          nativeUnits={nativeUnits}
          onNestingCreationFinish={onNestingCreationFinish}
          onNewMetaData={onNewMetaData}
          onNewSelectorCumulativeInfo={onNewSelectorCumulativeInfo}
          onlyShowHighlighted={onlyShowHighlighted}
          selectedBodies={selectedBodies}
          updatedVectors={updatedVectors}
          updatedVectorsCounter={updatedVectorsCounter}
          initializeVectorStyleByNameMap={initializeVectorStyleByNameMap}
          isDxfFinalizing={isDxfFinalizing}
          receiveSerializedDXF={receiveSerializedDXF}
          selectedObjectsKeys={selectedObjectsKeys}
          selectorToolSelections={selectorToolSelections}
          showAdditionalKeys={showAdditionalKeys}
          showAdditionalKeysExplicit={showAdditionalKeysExplicit}
          showHoverObject={showHoverObject}
          showSelectedObjects={showSelectedObjects}
          stockCreationFailed={stockCreationFailed}
          stockCreationSuccess={stockCreationSuccess}
          viewerMode={viewerMode}
          editorMode={editorMode}
          visibleBodies={visibleBodies}
          visibleStockResultKeys={visibleStockResultKeys}
          wireframe={wireframe}
          renderNativeColors={renderNativeColors}
          bodyPoseInformation={this.state.bodyPoseInformation}
          caliperObjects={this.state.caliperObjects}
          cameraPosition={this.state.cameraPosition}
          cameraUp={this.state.cameraUp}
          cameraZoom={this.state.cameraZoom}
          controlTarget={this.state.controlTarget}
          historicalCaliperObjects={this.state.historicalCaliperObjects}
          historicalSelectedObjects={this.state.historicalSelectedObjects}
          internalHighlight={this.state.internalHighlight}
          isFocused={this.state.isFocused}
          isTweening={this.state.isTweening}
          measurement={this.state.measurement}
          nodes={this.state.nodes}
          normals={this.state.normals}
          orthoView={this.state.orthoView}
          selectedObjects={this.state.selectedObjects}
          // viewer-component state management functions
          closePopover={this.closePopover}
          onBodyPoseChange={this.handleBodyPoseChange}
          onBoxSelect={this.handleBoxSelect}
          onBoxDeselect={this.handleBoxDeselect}
          onBoxSelectStart={this.handleBoxSelectStart}
          onBoxSelectMove={this.handleBoxSelectMove}
          onBoxSelectClear={this.handleBoxSelectClear}
          onCameraPositionChange={this.handleCameraPositionChange}
          onCameraUpChange={this.handleCameraUpChange}
          onCameraZoomChange={this.handleCameraZoomChange}
          onControlTargetChange={this.handleTargetChange}
          onFocusChange={this.handleFocusChange}
          onHasLoaded={this.onHasLoaded}
          onMaxBoundingBoxDiagonalChange={
            this.handleMaxBoundingBoxDiagonalChange
          }
          onNodeChange={this.handleNodeChange}
          onOrthoViewChange={this.handleOrthoViewChange}
          onRightClick={this.handleRightClickForPopover}
          onSimplified={this.onSimplified}
          temporarilySimplified={this.state.temporarilySimplified}
          tooComplex={this.state.tooComplex}
          onUpdateSelectionEntities={this.handleUpdateSelectionEntities}
          // parent component state management functions
          acknowledgeSnapTo={this.acknowledgeSnapTo}
          // mixed viewer-component state and parent component state management
          // Pseudo caliper selector is used for setting scale in new Vector Editor
          // It handles converting a selector selection to caliper selection
          onObjectClickForCaliperTool={
            editorMode === VECTOR_EDITOR_MODES.SCALE
              ? this.handlePseudoObjectClickForCaliperTool
              : this.handleObjectClickForCaliperTool
          }
          onObjectClickForSelectorTool={this.handleObjectClickForSelectorTool}
          onObjectHover={this.handleObjectHover}
          onSetMeasurement={this.handleSetMeasurement}
        />
        <ViewerAlerts
          temporarilySimplified={this.state.temporarilySimplified}
          tooComplex={this.state.tooComplex}
          failed={failedParts}
          numBodies={this.state.bodies.length}
          numParts={this.state.partOrAssemblyById.size}
        />
      </Wrapper>
    );
  }
}

export default ViewerComponent;
